// ─────────────────────────────────────────────────────────────────────────────
// ImportKnowledgeBaseDialogComponent
// ─────────────────────────────────────────────────────────────────────────────
// 2-step import dialog (Upload → Preview) reusable for aliases, external
// references, preferred products, and price observations.
// Accepts { type: 'alias' | 'external-reference' | 'preferred-product' | 'price-observation' }
// via MAT_DIALOG_DATA.
// Step 1: template download buttons + drag-and-drop file zone (CSV-only for
// preferred products; .msg/.eml/.zip and MULTI-FILE for price observations).
// Step 2: dry-run summary cards + row table with status badges; preferred
// products additionally show an overwrite warning (existing pairs are replaced).
// Closes with true on successful confirm import.
//
// Price observations differ from the other three on three points:
//   - multi-file: an Outlook export is a folder of messages, not one document;
//   - the preview is a sample, since the extraction is deferred;
//   - no scope question on an email export: a batch of .msg is multi-customer
//     by nature and its customers resolve per message.
// ─────────────────────────────────────────────────────────────────────────────

import { Component, Inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { SharedModule } from 'src/app/shared/shared.module';
import { UiModule } from 'src/app/ui/ui.module';
import { CommonService } from 'src/app/shared/common.service';
import { environment } from 'src/environments/environment';
import { PriceCandidatesStore } from '../../../quotations/services/price-candidates.store';
import { AliasService } from '../../services/alias.service';
import { ExternalReferenceService } from '../../services/external-reference.service';
import { PreferredProductService } from '../../services/preferred-product.service';
import { PriceObservationService } from '../../services/price-observation.service';
import { PriceColumnMappingStepComponent } from '../price-column-mapping-step/price-column-mapping-step.component';
import {
  AudienceScopePickerComponent,
  EMPTY_AUDIENCE_SCOPE,
  IAudienceScope,
  isAudienceIncomplete,
  PriceAudience,
} from '../audience-scope-picker/audience-scope-picker.component';
import { IImportPreviewResponse, IImportRow } from '../../models/import.models';
import {
  IPriceColumnMapping,
  IPriceImportInspection,
  IPriceImportReceipt,
  IPriceImportScopeSelection,
  IPriceObservationPreview,
  IPriceObservationPreviewRow,
  machineReasonLabel,
} from '../../models/price-observation.models';

export type ImportDialogType = 'alias' | 'external-reference' | 'preferred-product' | 'price-observation';

/** Everything that differs between the four import types, in one row each. */
interface ImportTypeConfig {
  /** i18n namespace under `knowledge_base.import.`. */
  i18n: string;
  /** API segment under BASE_URL_API, for the template download. */
  api: string;
  extensions: string[];
  /** Only the email channel drops a whole batch at once. */
  multiple: boolean;
}

const SPREADSHEET_EXTENSIONS = ['.csv', '.xls', '.xlsx'];

/**
 * The spreadsheets the mapping screen can read -- not
 * `SPREADSHEET_EXTENSIONS`, which is the tabular channels' `accept` attribute.
 */
const MAPPABLE_SPREADSHEET_EXTENSIONS = ['.csv', '.xlsx', '.xlsm'];

const isMappableSpreadsheet = (name: string | null | undefined): boolean => {
  const lower = (name ?? '').toLowerCase();
  return MAPPABLE_SPREADSHEET_EXTENSIONS.some((ext) => lower.endsWith(ext));
};

/**
 * The standalone-document channel: a PDF price list deposited on its own. Read
 * by ATO's gated model, so every line lands `to_verify`, and the operator says
 * who it concerns because a bare document names nobody.
 */
const isPriceDocument = (name: string | null | undefined): boolean =>
  (name ?? '').toLowerCase().endsWith('.pdf');

/**
 * One message, not a batch of them.
 *
 * `.zip` is absent on purpose: an archive is multi-customer by nature, so one
 * audience for the batch would overwrite the real attribution and the server
 * refuses it. A single .eml or .msg is one thread with one counterparty, and
 * "for everyone" is a statement about that thread -- without it a supplier's
 * public tariff is filed as one buyer's negotiated rate.
 */
const MESSAGE_EXTENSIONS = ['.eml', '.msg'];

const isSingleMessage = (name: string | null | undefined): boolean => {
  const lower = (name ?? '').toLowerCase();
  return MESSAGE_EXTENSIONS.some((ext) => lower.endsWith(ext));
};

/** Stable identities for `audienceLabelKeys` — see the getter. */
const MESSAGE_LABEL_KEYS: Partial<Record<PriceAudience, string>> = {
  file: 'knowledge_base.import.scope.from_message',
};
const NO_LABEL_KEYS: Partial<Record<PriceAudience, string>> = {};

/** One drop is one channel. The channel of one file, for the mix check. */
const channelOf = (name: string): 'spreadsheet' | 'document' | 'email' => {
  if (isMappableSpreadsheet(name)) return 'spreadsheet';
  if (isPriceDocument(name)) return 'document';
  return 'email';
};

/** The four types, side by side; the Record refuses a missing row. */
const IMPORT_TYPES: Record<ImportDialogType, ImportTypeConfig> = {
  alias: {
    i18n: 'aliases',
    api: 'ai/kb/aliases',
    extensions: SPREADSHEET_EXTENSIONS,
    multiple: false,
  },
  'external-reference': {
    i18n: 'external_references',
    api: 'ai/kb/external-references',
    extensions: SPREADSHEET_EXTENSIONS,
    multiple: false,
  },
  'preferred-product': {
    i18n: 'preferred_products',
    api: 'ai/kb/preferred-products',
    extensions: SPREADSHEET_EXTENSIONS,
    multiple: false,
  },
  'price-observation': {
    i18n: 'price_observations',
    // The email channel: .msg / .eml and .zip for a whole export, plus the
    // spreadsheets the mapping screen makes readable, plus .pdf for a
    // standalone price document.
    api: 'ai/kb/pricing-observations',
    extensions: ['.msg', '.eml', '.zip', '.xlsx', '.xlsm', '.csv', '.pdf'],
    multiple: true,
  },
};

export interface ImportDialogConfig {
  type: ImportDialogType;
}

// Two steps for every type, and a third the price channel reaches only when a
// spreadsheet does not follow the template: it is mapped, not refused. The
// audience question lives inside the drop step, since it is asked of every
// price file, mapped or not.
type ImportStep = 'upload' | 'mapping' | 'preview';
type BadgeType = 'ok' | 'warning' | 'duplicate' | 'error';

/**
 * What the four import endpoints answer with.
 *
 * The three row-based imports put their dry-run in `object`; the price import
 * puts its preview there and, on confirm, the queued journal rows in `data`.
 * Declared once so `dispatchImport` returns a single observable type: a union
 * of the four service signatures is not callable.
 */
type ImportDispatchResponse = {
  object?: IImportPreviewResponse | IPriceObservationPreview;
  data?: IPriceImportReceipt[];
};

@Component({
  selector: 'app-import-knowledge-base-dialog',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatProgressSpinnerModule,
    SharedModule,
    UiModule,
    AudienceScopePickerComponent,
    PriceColumnMappingStepComponent,
  ],
  templateUrl: './import-knowledge-base-dialog.component.html',
  styleUrls: ['./import-knowledge-base-dialog.component.scss'],
})
export class ImportKnowledgeBaseDialogComponent implements OnDestroy {
  // ── Dialog config ─────────────────────────────────────────────────────────

  readonly type: ImportDialogType;

  // ── Step state ────────────────────────────────────────────────────────────

  currentStep: ImportStep = 'upload';

  // ── Upload step ───────────────────────────────────────────────────────────

  /** Price observations accept a batch; the other three types keep one file. */
  selectedFiles: File[] = [];
  /**
   * Derived from the selection, written only by `setSelection`.
   *
   * Fields and not getters: the template reads both on every change-detection
   * pass, and an Outlook export is hundreds of files to iterate each time.
   */
  hasSpreadsheet = false;
  /** True when the selection holds a standalone price document (.pdf). */
  hasDocument = false;
  /**
   * True when the selection holds one message rather than a batch of them.
   *
   * The server refuses an audience on an archive, which is multi-customer by
   * nature. A single .eml or .msg is one thread, and "everyone" is a statement
   * about it.
   */
  hasMessage = false;
  fileNames = '';
  /** The single-file view of the selection. Always `selectedFiles[0]`. */
  get selectedFile(): File | null {
    return this.selectedFiles[0] ?? null;
  }
  isDragOver = false;
  fileError = '';

  // ── Preview step ──────────────────────────────────────────────────────────

  previewData: IImportPreviewResponse | null = null;
  /** The sampled preview of a price-source import. */
  pricePreview: IPriceObservationPreview | null = null;
  isLoadingPreview = false;
  isConfirming = false;

  // ── "These prices concern" (inside the drop step) ─────────────────────────
  //
  // Four answers, and "the file says who" is the default: the template has a
  // `customer` column the server resolves row by row, and a row whose code does
  // not resolve lands "Unknown customer" rather than somebody else's.
  //
  // The other three are the knowledge base's own -- everyone, one customer,
  // named groups -- asked with the KB's own picker. A standalone PDF has no
  // customer column, so this is the only way to state its audience.

  readonly scopeAudiences: PriceAudience[] = ['file', 'global', 'customer', 'group'];

  /**
   * The audiences a message may be given: "the message says who", or everyone.
   *
   * `customer` and `group` are absent on purpose: naming one customer for a
   * thread whose counterparty is known lets the upload overrule the message,
   * and the server refuses it too.
   *
   * `file` reads as "the message says who" here -- the thread's counterparty is
   * read, never an attached tariff's own `customer` column -- which is why the
   * i18n key is chosen on `hasMessage`.
   */
  readonly messageAudiences: PriceAudience[] = ['file', 'global'];

  audienceScope: IAudienceScope = {
    ...EMPTY_AUDIENCE_SCOPE,
    audience: 'file',
  };

  /** The picker's answer in the shape the upload sends it. */
  get scopeSelection(): IPriceImportScopeSelection {
    return {
      scope: this.audienceScope.audience,
      customer_ids: this.audienceScope.customers.map((customer) => customer.id),
      group_ids: this.audienceScope.groupIds,
    };
  }

  // ── Template conformity ──────────────────────────────────────────────────

  isInspecting = false;
  /**
   * The server's refusal: the file does not follow the template.
   *
   * The door to the mapping screen, not a dead end. The whole answer is kept,
   * not only the missing columns, because the same payload carries the detected
   * header and the sample the mapping step renders. A refusal it cannot answer
   * -- a sheet with no rows -- has no `labels`, and CONTINUE stays off.
   */
  templateRefusal: IPriceImportInspection | null = null;
  /** The mapping the operator confirmed, travelling with the upload. */
  confirmedMapping: IPriceColumnMapping | null = null;

  // ── Cleanup ───────────────────────────────────────────────────────────────

  private destroy$ = new Subject<void>();
  /**
   * Fires when a new drop makes the inspection in flight answer a question
   * nobody is asking. Separate from `destroy$`: the dialog stays open, only
   * the request is dropped.
   */
  private inspectionSuperseded$ = new Subject<void>();

  // ── Allowed file types ───────────────────────────────────────────────────

  private get acceptedTypes(): string[] {
    return ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
  }

  private get typeConfig(): ImportTypeConfig {
    return IMPORT_TYPES[this.type];
  }

  private get acceptedExtensions(): string[] {
    return this.typeConfig.extensions;
  }

  get acceptAttr(): string {
    return this.acceptedExtensions.join(',');
  }

  get allowsMultipleFiles(): boolean {
    return this.typeConfig.multiple;
  }

  constructor(
    public dialogRef: MatDialogRef<ImportKnowledgeBaseDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private config: ImportDialogConfig,
    private aliasService: AliasService,
    private externalReferenceService: ExternalReferenceService,
    private preferredProductService: PreferredProductService,
    private priceObservationService: PriceObservationService,
    private priceCandidates: PriceCandidatesStore,
    private commonService: CommonService,
    private translateService: TranslateService,
  ) {
    this.type = config.type;
  }

  ngOnDestroy(): void {
    this.inspectionSuperseded$.next();
    this.inspectionSuperseded$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Computed helpers ──────────────────────────────────────────────────────

  get titleKey(): string {
    return `knowledge_base.import.${this.typeConfig.i18n}.title`;
  }

  get infoKey(): string {
    return `knowledge_base.import.${this.typeConfig.i18n}.info`;
  }

  private get apiSegment(): string {
    return this.typeConfig.api;
  }

  get csvTemplateUrl(): string {
    return `${environment.BASE_URL_API}${this.apiSegment}/import/template?format=csv`;
  }

  get xlsxTemplateUrl(): string {
    return `${environment.BASE_URL_API}${this.apiSegment}/import/template?format=xlsx`;
  }

  get isAliasType(): boolean {
    return this.type === 'alias';
  }

  get isPreferredProductType(): boolean {
    return this.type === 'preferred-product';
  }

  get isPriceObservationType(): boolean {
    return this.type === 'price-observation';
  }

  /**
   * A template only helps while the operator is writing the file, which is
   * before they have one to select.
   *
   * An email export comes out of Outlook, so once one is dropped the template
   * is noise. Before that the price channel accepts both and cannot know which
   * is coming, so it offers -- proposed, never imposed.
   */
  get showsTemplate(): boolean {
    if (!this.isPriceObservationType) return true;
    return !this.selectedFiles.length || this.hasSpreadsheet;
  }

  /**
   * The accepted extensions, spelled out for a human. Derived from the list the
   * validator and the `accept` attribute use, so it cannot drift from the rule.
   */
  get acceptedExtensionsLabel(): string {
    return this.typeConfig.extensions.join(', ');
  }


  /**
   * True when the batch cannot receive an import scope. Driven by the backend's
   * answer, so this component does not have to know the format rules.
   */
  get scopeIsForbidden(): boolean {
    return this.isPriceObservationType && this.pricePreview?.scope_allowed === false;
  }

  /** The audiences the picker may offer for THIS upload. */
  get availableAudiences(): PriceAudience[] {
    return this.hasMessage ? this.messageAudiences : this.scopeAudiences;
  }

  /**
   * "The file says who" reads "the message says who" on a message.
   *
   * Both branches are constants: a fresh object literal re-fires the picker's
   * `set labelKeys` on every change-detection cycle, re-running a
   * `translate.instant()` per audience.
   */
  get audienceLabelKeys(): Partial<Record<PriceAudience, string>> {
    return this.hasMessage ? MESSAGE_LABEL_KEYS : NO_LABEL_KEYS;
  }

  get totalRows(): number {
    return this.previewData?.summary.rows_processed ?? 0;
  }

  get validCount(): number {
    return this.previewData?.summary.valid ?? 0;
  }

  get warningCount(): number {
    return this.previewData?.summary.warning ?? 0;
  }

  get duplicates(): number {
    return this.previewData?.summary.skipped ?? 0;
  }

  get errors(): number {
    return this.previewData?.summary.invalid ?? 0;
  }

  // ── Price-observation preview counters ────────────────────────────────────

  get sampleRows(): IPriceObservationPreviewRow[] {
    return this.pricePreview?.sample ?? [];
  }

  get sampleSize(): number {
    return this.pricePreview?.summary.sample_size ?? this.sampleRows.length;
  }

  get linesExtracted(): number {
    return this.pricePreview?.summary.lines_extracted ?? 0;
  }

  get resolvedCustomerCount(): number {
    return this.pricePreview?.summary.resolved_customer ?? 0;
  }

  get resolvedProductCount(): number {
    return this.pricePreview?.summary.resolved_product ?? 0;
  }

  /** The lines that will land "Unknown customer, to correct". */
  get unknownCustomerCount(): number {
    return this.pricePreview?.summary.unknown_customer ?? 0;
  }

  get previewError(): string {
    return this.pricePreview?.error ?? '';
  }

  /**
   * CONFIRM never stays active over a preview in error, nor over one that read
   * nothing: an accepted upload would grow a journal row for a file that
   * produced nothing, and the emptiness is already known here.
   */
  get canConfirm(): boolean {
    return !this.isConfirming && !this.previewError && !this.emptyPreview;
  }

  /** Said next to a disabled CONFIRM, so the button explains itself. */
  get emptyPreview(): boolean {
    return this.isPriceObservationType && !this.previewError && this.linesExtracted === 0;
  }


  get hasSelection(): boolean {
    return this.selectedFiles.length > 0;
  }

  // ── Template download ─────────────────────────────────────────────────────

  downloadTemplate(format: 'csv' | 'xlsx'): void {
    const url = format === 'csv' ? this.csvTemplateUrl : this.xlsxTemplateUrl;
    window.open(url, '_blank');
  }

  // ── File handling ─────────────────────────────────────────────────────────

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    this.handleFileList(event.dataTransfer?.files ?? null);
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.handleFileList(input.files);
    input.value = '';
  }

  private handleFileList(list: FileList | null): void {
    const files = Array.from(list ?? []);
    if (!files.length) return;

    this.fileError = '';
    const rejected = files.filter((file) => !this.isValidFileType(file));
    if (rejected.length) {
      // The parametrized wording is the price import's own; the three others
      // keep their historical sentence.
      this.fileError = this.translateService.instant(
        this.isPriceObservationType
          ? 'knowledge_base.import.invalid_file_type_prices'
          : 'knowledge_base.import.invalid_file_type',
        { extensions: this.acceptedExtensionsLabel },
      );
      this.setSelection([]);
      return;
    }

    // One drop is one channel: spreadsheets, standalone documents and e-mail
    // exports follow different regimes and do not mix in a batch.
    if (this.isPriceObservationType && files.length > 1) {
      const channels = new Set(files.map((file) => channelOf(file.name)));
      if (channels.size > 1) {
        this.fileError = this.translateService.instant(
          'knowledge_base.import.price_observations.no_mixing',
        );
        this.setSelection([]);
        return;
      }
    }

    this.setSelection(this.allowsMultipleFiles ? files : [files[0]]);
    // The conformity question is asked at the drop, so the refusal and the
    // template link show before any CONTINUE.
    this.inspectSelection();
  }

  /** The only place the selection is written, so nothing derived can drift. */
  private setSelection(files: File[]): void {
    // The inspection is derived from the selection, so it drops here with the
    // rest, request included. The refusal paths above write the selection and
    // never reach `inspectSelection`, so an earlier answer would land on an
    // empty selection and open the mapping screen on nothing.
    this.inspectionSuperseded$.next();
    this.templateRefusal = null;
    this.confirmedMapping = null;
    this.isInspecting = false;

    this.selectedFiles = files;
    this.hasSpreadsheet = files.some((file) => isMappableSpreadsheet(file?.name));
    this.hasDocument = files.some((file) => isPriceDocument(file?.name));
    this.hasMessage = files.some((file) => isSingleMessage(file?.name));
    this.fileNames = files.map((file) => file.name).join(', ');

    // The channel decides which audiences exist -- a message offers neither
    // `customer` nor `group`. Carried over to the .eml that replaced a
    // spreadsheet, the answer posts a scope the server silently drops, with
    // CONTINUE lit on an option the select no longer shows.
    const audience = this.audienceScope.audience;
    if (audience && !this.availableAudiences.includes(audience)) {
      this.audienceScope = { ...EMPTY_AUDIENCE_SCOPE, audience: 'file' };
    }
  }

  private isValidFileType(file: File): boolean {
    const name = file.name.toLowerCase();
    if (this.acceptedExtensions.some((ext) => name.endsWith(ext))) {
      return true;
    }
    // Only the spreadsheet types have a usable MIME fallback: every browser
    // reports a .msg as application/octet-stream, so the extension is the only
    // signal there.
    if (this.isPriceObservationType) {
      return false;
    }
    return this.acceptedTypes.includes(file.type) && !name.endsWith('.xls') && !name.endsWith('.xlsx');
  }

  // ── Step navigation ───────────────────────────────────────────────────────

  /**
   * The audience block shows for a price spreadsheet and a standalone document,
   * which name nobody by themselves; an e-mail carries its counterparty.
   */
  get needsScope(): boolean {
    return (
      this.isPriceObservationType
      && (this.hasSpreadsheet || this.hasDocument || this.hasMessage)
    );
  }

  /**
   * CONTINUE stays unavailable until the audience question is answered:
   * choosing "one customer" is not an answer until a customer is named, or the
   * batch publishes under the file's own attribution.
   */
  get scopeAnswered(): boolean {
    if (!this.needsScope) return true;
    return this.audienceScope.audience != null && !isAudienceIncomplete(this.audienceScope);
  }

  /** The consequence of the chosen answer, when the wording has one. */
  get scopeHintKey(): string | null {
    if (this.audienceScope.audience === 'file') {
      // On a message the thread's counterparty is read and the attachment's own
      // `customer` column is dropped, so the same option needs another
      // sentence.
      return this.hasMessage
        ? 'knowledge_base.import.scope.from_message_hint'
        : 'knowledge_base.import.scope.from_file_hint';
    }
    if (this.audienceScope.audience === 'global') {
      return this.hasMessage
        ? 'knowledge_base.import.scope.global_message_hint'
        : 'knowledge_base.import.scope.global_hint';
    }
    return null;
  }

  onAudienceScopeChange(scope: IAudienceScope): void {
    this.audienceScope = scope;
  }

  /**
   * upload -> (mapping) -> preview.
   *
   * A refusal a mapping can answer routes through the mapping step; one it
   * cannot -- a sheet with no rows -- stops here, which is why CONTINUE tests
   * `canMap` rather than "is there a refusal".
   */
  next(): void {
    if (!this.hasSelection || !this.scopeAnswered) return;
    if (this.templateRefusal && !this.confirmedMapping) {
      if (!this.canMap) return;
      this.currentStep = 'mapping';
      return;
    }
    this.proceed();
  }

  /** True when the refusal carries a question the mapping screen can ask. */
  get canMap(): boolean {
    return !!this.templateRefusal?.labels?.length;
  }

  /**
   * The audience as the mapping screen reads it: it needs to know whether "the
   * customer is nowhere in the file" has an answer.
   */
  get mappingScope(): PriceAudience | null {
    return this.audienceScope.audience;
  }

  onMappingConfirmed(mapping: IPriceColumnMapping): void {
    this.confirmedMapping = mapping;
    this.proceed();
  }

  back(): void {
    // From the preview of a mapped file, back is the mapping screen: fixing a
    // column is likelier than re-dropping the file.
    if (this.currentStep === 'preview' && this.confirmedMapping) {
      this.currentStep = 'mapping';
      return;
    }
    this.currentStep = 'upload';
  }

  /**
   * Ask the server whether the dropped spreadsheet follows the template. The
   * refusal, with the missing columns named by the server, is shown at the drop
   * step and CONTINUE stays off.
   */
  private inspectSelection(): void {
    // Nothing to reset here: `setSelection` cancelled the inspection in flight
    // and cleared what it produced. On `destroy$` alone, a slow answer about a
    // replaced file still lands and opens the mapping screen on it.
    const spreadsheets = this.selectedFiles.filter((candidate) =>
      isMappableSpreadsheet(candidate.name),
    );
    if (!spreadsheets.length || !this.isPriceObservationType) {
      return;
    }

    this.isInspecting = true;
    forkJoin(
      spreadsheets.map((file) => this.priceObservationService.inspectPriceFile(file)),
    )
      .pipe(takeUntil(this.inspectionSuperseded$), takeUntil(this.destroy$))
      .subscribe({
        next: (responses) => {
          this.isInspecting = false;
          const refused = responses
            .map((response) => response.object)
            .filter((inspection) => inspection && !inspection.conform);
          if (!refused.length) {
            return;
          }
          // One mapping travels with the whole batch, so it answers for one
          // layout. Every spreadsheet is inspected, not just the first: the
          // first file's answer applied to the others reads the second file's
          // columns through the first file's map.
          if (spreadsheets.length > 1) {
            this.fileError = this.translateService.instant(
              'knowledge_base.import.price_observations.one_mapping_one_file',
            );
            this.setSelection([]);
            return;
          }
          this.templateRefusal = refused[0];
        },
        error: () => {
          // Reading the header failed, and it is said out loud: nothing on
          // screen reads as "the file is fine", which is the one thing this
          // call could not establish.
          this.isInspecting = false;
          this.showError('knowledge_base.import.toast.inspect_error');
        },
      });
  }

  proceed(): void {
    if (!this.hasSelection) return;
    this.isLoadingPreview = true;

    const import$ = this.dispatchImport(this.buildFormData(), true);

    import$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        if (this.isPriceObservationType) {
          this.pricePreview = response.object as IPriceObservationPreview;
        } else {
          this.previewData = response.object as IImportPreviewResponse;
        }
        this.isLoadingPreview = false;
        this.currentStep = 'preview';
      },
      error: () => {
        this.isLoadingPreview = false;
        this.showError('knowledge_base.import.toast.preview_error');
      },
    });
  }

  // ── Confirm import ────────────────────────────────────────────────────────

  confirmImport(): void {
    if (!this.hasSelection || this.isConfirming) return;
    this.isConfirming = true;

    const import$ = this.dispatchImport(this.buildFormData(), false);

    import$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.isConfirming = false;
        // A price import is accepted, not completed: the extraction is deferred,
        // the toast says so and the tab shows the report.
        const key = this.isPriceObservationType
          ? 'knowledge_base.import.toast.import_queued'
          : 'knowledge_base.import.toast.import_success';
        if (this.isPriceObservationType) {
          // A confirmed import changes what the engine answers; the order
          // screens must re-ask instead of serving their memo.
          this.priceCandidates.invalidate();
        }
        this.commonService.showToast(this.translateService.instant(key), 'SUCCESS');
        // Hand the journal ids back, not a bare `true`: the extraction has not
        // run yet, so the caller can wait for them (`awaitImports`) instead of
        // reloading a table that predates the upload.
        this.dialogRef.close(this.queuedImportIds(response) ?? true);
      },
      error: () => {
        this.isConfirming = false;
        this.showError('knowledge_base.import.toast.error');
      },
    });
  }

  /**
   * A failed call says so: resetting a flag and nothing else makes a dropped
   * file, a failed dry-run and a failed upload all look like a dialog that has
   * stopped responding.
   */
  private showError(key: string): void {
    this.commonService.showToast(this.translateService.instant(key), 'ERROR');
  }

  private buildFormData(): FormData {
    const formData = new FormData();
    this.selectedFiles.filter(Boolean).forEach((file) => formData.append('ufile', file));
    if (this.needsScope && this.scopeSelection.scope) {
      // Only for a spreadsheet or a standalone document: the backend refuses a
      // scope on an email export, whose customers resolve per message.
      this.priceObservationService.appendScope(formData, this.scopeSelection);
    }
    if (this.confirmedMapping) {
      this.priceObservationService.appendMapping(formData, this.confirmedMapping);
    }
    return formData;
  }

  /**
   * The journal ids the upload just created, or null for the other three import
   * types: they are synchronous and have nothing to wait for.
   */
  private queuedImportIds(response: ImportDispatchResponse): number[] | null {
    if (!this.isPriceObservationType) return null;
    const ids = (response.data ?? []).map((receipt) => receipt.import_id).filter((id) => id != null);
    return ids.length ? ids : null;
  }

  private dispatchImport(formData: FormData, dryRun: boolean): Observable<ImportDispatchResponse> {
    switch (this.type) {
      case 'alias':
        return this.aliasService.importAliases(formData, dryRun);
      case 'preferred-product':
        return this.preferredProductService.importPreferredProducts(formData, dryRun);
      case 'price-observation':
        return this.priceObservationService.importPriceSources(formData, dryRun);
      default:
        return this.externalReferenceService.importExternalReferences(formData, dryRun);
    }
  }

  // ── Row helpers ───────────────────────────────────────────────────────────

  getBadgeType(row: IImportRow): BadgeType {
    switch (row.status) {
      case 'valid':
        return 'ok';
      case 'warning':
        return 'warning';
      case 'skipped':
        return 'duplicate';
      case 'invalid':
        return 'error';
    }
  }

  getBadgeLabel(row: IImportRow): string {
    switch (row.status) {
      case 'valid':
        return this.translateService.instant('knowledge_base.import.status.ok');
      case 'warning':
        return this.translateService.instant('knowledge_base.import.status.warning');
      case 'skipped':
        return this.translateService.instant('knowledge_base.import.status.duplicate');
      case 'invalid':
        return this.translateService.instant('knowledge_base.import.status.error');
    }
  }

  getRowReason(row: IImportRow): string {
    if (row.reason) {
      return this.translateReason(row.reason, row.field, row.value);
    }
    if (row.warnings?.length) {
      return row.warnings.map((w) => this.translateReason(w)).join(', ');
    }
    return '—';
  }

  private translateReason(reason: string, field?: string, value?: string): string {
    const key = `knowledge_base.import.reason.${reason}`;
    const translatedField = field ? this.translateService.instant(`knowledge_base.import.field_name.${field}`) : field;
    const translated = this.translateService.instant(key, {
      field: translatedField ?? '',
      value: value ?? '',
    });
    if (translated !== key) return translated;
    // The machine reasons a price line carries are named once, where the import
    // journal reads them: a second copy of the lookup would be a second
    // wording. Falls back to the raw reason, never a silent blank.
    return machineReasonLabel(this.translateService, reason);
  }

  // ── Price-observation row helpers ─────────────────────────────────────────

  /**
   * A sampled line is `ok` only when both the customer and the product
   * resolved: anything less is work waiting for an operator.
   */
  getSampleBadge(row: IPriceObservationPreviewRow): { type: BadgeType; label: string } {
    const [type, key]: [BadgeType, string] =
      row.scope === 'unresolved' ? ['error', 'unresolved_customer']
      : row.state === 'to_verify' ? ['warning', 'to_verify']
      : ['ok', 'ok'];
    return { type, label: this.translateService.instant(`knowledge_base.import.status.${key}`) };
  }

  getSampleReason(row: IPriceObservationPreviewRow): string {
    return row.reason ? this.translateReason(row.reason) : '—';
  }

  getSampleCustomer(row: IPriceObservationPreviewRow): string {
    return row.customer_name ?? '—';
  }

  getSampleProduct(row: IPriceObservationPreviewRow): string {
    return row.product_id_name ?? row.product_name ?? row.product_code ?? '—';
  }

  getSamplePrice(row: IPriceObservationPreviewRow): string {
    if (row.price == null) return '—';
    return row.currency ? `${row.price} ${row.currency}` : `${row.price}`;
  }

  getDisplaySku(row: IImportRow): string {
    return row.data?.sku ?? '—';
  }

  getDisplayAlias(row: IImportRow): string {
    return row.data?.alias_value ?? '—';
  }

  getDisplayReferenceCode(row: IImportRow): string {
    return row.data?.reference_code ?? '—';
  }

  getDisplayCustomer(row: IImportRow): string {
    return row.data?.customer ?? row.data?.customer_id ?? '—';
  }

  getDisplayProduct(row: IImportRow): string {
    return row.data?.sku ?? row.data?.product_id ?? '—';
  }

  // ── Close ─────────────────────────────────────────────────────────────────

  close(): void {
    this.dialogRef.close(false);
  }
}
